/**
 * Utility functions for processing guideline updates DITA content
 */

/**
 * Extracts guideline name from a URL pathname
 * @param {string} pathname - The URL pathname (e.g., "/guidelines/Antibiotic/updates")
 * @returns {string|null} - The guideline name or null if not found
 */
export const extractGuidelineNameFromPath = (pathname) => {
  if (!pathname || typeof pathname !== "string") {
    return null;
  }

  const match = pathname.match(/^\/(?:guidelines|resources)\/([^/]+)/);
  return match ? match[1] : "";
};

/**
 * Checks if a DITA chapter element is a relationship table
 * @param {Element} chapter - The chapter DOM element
 * @returns {boolean} - True if chapter is a relationship table
 */
export const isRelationshipTableChapter = (chapter) => {
  if (!chapter) {
    return false;
  }

  const mapref = chapter.getElementsByTagName("mapref")[0];
  return Boolean(mapref && mapref.getAttribute("href") === "Updates_Reltable.ditamap");
};

/**
 * Extracts topic references from a DITA chapter element
 * @param {Element} chapter - The chapter DOM element
 * @returns {Array} - Array of topicref elements (filtered to exclude nested maprefs)
 */
export const extractTopicRefsFromChapter = (chapter) => {
  if (!chapter) {
    return [];
  }

  const topicrefs = chapter.getElementsByTagName("topicref");

  return Array.from(topicrefs).filter((ref) => {
    // Filter out any nested maprefs or empty refs
    return ref.getAttribute("href") && !ref.closest("mapref");
  });
};

/**
 * Extracts navtitle from a DITA map element (chapter/topicref)
 * @param {Element} element - The DOM element
 * @returns {string} - The navtitle text or empty string
 */
export const extractNavTitleFromElement = (element) => {
  if (!element) {
    return "";
  }

  const navtitleAttr = element.getAttribute("navtitle");
  if (navtitleAttr) {
    return navtitleAttr;
  }

  const navtitleElement = element.getElementsByTagName("navtitle")[0];
  return navtitleElement?.textContent || "";
};

/**
 * Processes DITA chapters into structured update data
 * @param {HTMLCollection} chapters - Collection of chapter elements from ditamap
 * @returns {Array} - Array of chapter objects with dateHref and updates
 */
export const processChaptersToUpdates = (chapters) => {
  if (!chapters || chapters.length === 0) {
    return [];
  }

  const updatesArray = [];

  Array.from(chapters).forEach((chapter) => {
    // Skip relationship table chapters
    if (isRelationshipTableChapter(chapter)) {
      return;
    }

    // Get the date heading file
    const chapterHref = chapter.getAttribute("href");
    if (!chapterHref) {
      return;
    }

    const chapterTitle = extractNavTitleFromElement(chapter);

    // Get all topic references under this chapter
    const topicRefs = extractTopicRefsFromChapter(chapter);

    // Only add chapters that have updates
    if (topicRefs.length > 0) {
      updatesArray.push({
        dateHref: chapterHref,
        dateTitle: chapterTitle,
        updates: topicRefs,
      });
    }
  });

  return updatesArray;
};

/**
 * Checks if DITA content has a hidden heading metadata
 * @param {Document} ditaDocument - Parsed DITA XML document
 * @returns {boolean} - True if heading should be hidden
 */
export const hasHiddenHeadingMetadata = (ditaDocument) => {
  if (!ditaDocument) {
    return false;
  }

  const othermetas = ditaDocument.getElementsByTagName("othermeta");

  for (let i = 0; i < othermetas.length; i++) {
    const meta = othermetas[i];
    if (meta.getAttribute("name") === "hidden" && meta.getAttribute("content") === "heading") {
      return true;
    }
  }

  return false;
};

/**
 * Extracts title from DITA document
 * @param {Document} ditaDocument - Parsed DITA XML document
 * @returns {string} - The title text or empty string
 */
export const extractTitleFromDita = (ditaDocument) => {
  if (!ditaDocument) {
    return "";
  }

  const titleElement = ditaDocument.getElementsByTagName("title")[0];
  let titleContent = titleElement?.textContent || "";

  if (!titleContent) {
    // sometime the <title> becomes a <ditatitle> and we need to support both. Use <ditatitle> as fallback
    const ditatitleElement = ditaDocument.getElementsByTagName("ditatitle")[0];
    titleContent = ditatitleElement?.textContent || "";
  }
  return titleContent;
};

/**
 * Extracts conbody HTML from DITA document
 * @param {Document} ditaDocument - Parsed DITA XML document
 * @returns {string} - The conbody HTML or empty string
 */
export const extractConbodyFromDita = (ditaDocument) => {
  if (!ditaDocument) {
    return "";
  }

  const conbodyElement = ditaDocument.getElementsByTagName("conbody")[0];
  return conbodyElement?.innerHTML || "";
};

/**
 * Validates if a guideline name is valid
 * @param {string} guidelineName - The guideline name to validate
 * @returns {boolean} - True if valid
 */
export const isValidGuidelineName = (guidelineName) => {
  return Boolean(
    guidelineName && typeof guidelineName === "string" && guidelineName.trim().length > 0,
  );
};
